import type { AuthStrategy, CollectionConfig, TypedUser } from 'payload'
import { parseCookieHeader } from './cookies.js'
import {
    wlodevCloudEnabled,
    wlodevAccessPath,
    wlodevRefreshPath,
    wlodevHostHeader,
} from '../config.js'
import { getFetchURL } from '../utils.js'

export const wlodevAuthStrategy = (
    collection: CollectionConfig,
): AuthStrategy => ({
    name: 'wlodev',
    authenticate: async ({ headers, payload }) => {
        // This part is a bit counter-intuitive as we load the local payload user despite not veryfing if the environment
        // is the cloud and if it is, we don't check the user authorized before. This is because of the payloadcms showing the welcome
        // screen to put the email address and password when there are no users, so we can do that first and then check if we can return user object based on authorization
        //
        // This is solution for now, but i think there's a better way to do that, like creating user on bootstraping the payloadcms app
        // Now, the drawback is that everytime the outside users try to  access the cms without valid tokens, we query the mongodb
        const users = await payload.find({
            collection: collection.slug,
        })

        // Response user
        let user: TypedUser = null
        if (users.docs && users.docs.length) {
            user = users.docs[0] as TypedUser
            user.collection = collection.slug
        } else {
            user = (await payload.create({
                collection: collection.slug,
                data: {},
            })) as TypedUser
            user.collection = collection.slug
        }

        if (wlodevCloudEnabled) {
            const accessURL = getFetchURL(headers, wlodevAccessPath)
            accessURL.searchParams.append(
                'domain',
                headers.get(wlodevHostHeader),
            )

            const accessRes = await fetch(accessURL, {
                headers: {
                    cookie: headers.get('cookie'),
                },
            })
            if (accessRes.ok) {
                try {
                    // Prevent parsing an empty string
                    // so the code wont break with TypeError
                    const responseText = await accessRes.text()
                    const data =
                        responseText === '' ? {} : JSON.parse(responseText)

                    if (data.siteId) {
                        return {
                            user,
                        }
                    }
                } catch (err) {
                    payload.logger.error(`wlodev access request error: ${err}`)
                    return {
                        user: null,
                    }
                }
            }

            // If we don't have the user at this point, we can assume that tokens have expired
            // and we can try to refresh them. In order not to make the request every time
            // we also naivly check if the user has the refresh token in cookies
            const cookieHeader = headers.get('cookie')
            if (!cookieHeader || !cookieHeader.length) {
                return {
                    user: null,
                }
            }

            const requestCookies = parseCookieHeader(headers.get('cookie'))
            if (!requestCookies['RefreshToken']) {
                return {
                    user: null,
                }
            }

            const refreshRes = await fetch(
                getFetchURL(headers, wlodevRefreshPath),
                {
                    method: 'POST',
                    headers: {
                        cookie: headers.get('cookie'),
                    },
                },
            )
            if (!refreshRes.ok) {
                return {
                    user: null,
                }
            }

            try {
                // Prevent parsing an empty string
                // so the code wont break with TypeError
                const refreshText = await refreshRes.text()
                const refreshData =
                    refreshText === '' ? {} : JSON.parse(refreshText)

                if (
                    !refreshData.idToken ||
                    !refreshData.accessToken ||
                    !refreshData.expiresIn
                ) {
                    payload.logger.error(`wlodev malformed refresh response`)
                    return {
                        user: null,
                    }
                }

                // We need to copy the set-cookie header from the tokensRes which properly
                // add or delete cookie with proper domain and security
                const tokensSetCookieHeader = headers.get('set-cookie')
                if (!tokensSetCookieHeader) {
                    payload.logger.error(
                        `wlodev refresh response does not contain set-cookie header`,
                    )
                    return {
                        user: null,
                    }
                }

                // Set the new tokens
                const newHeaders = new Headers()
                newHeaders.append('set-cookie', tokensSetCookieHeader)
                return {
                    user,
                    responseHeaders: newHeaders,
                }
            } catch (err) {
                payload.logger.error(`wlodev refresh request error: ${err}`)
                return {
                    user: null,
                }
            }
        }

        return {
            user,
        }
    },
})
